import {Moment} from 'moment';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {
  RECOMMENDATION_TYPES,
  SPP_RECOMMENDATION_TYPES,
} from '../../../constants/SolutionType';
import {ListGoalBasic_goals} from '../../../schema-types/ListGoalBasic';
import type {setUpNewFields} from '../RecommendsSetUpNew/setUpNewFields';
import type {modifyFieldsType} from '../RecommendModify/modifyFieldTypes';
import type {contributeFieldType} from '../RecommendsContribute/contributeFields';
import type {transferFieldsType} from '../RecommendsTransfer/transferFields';
import type {withdrawalFieldsType} from '../RecommendsWithdrawal/withdrawalFields';
import type {actionFieldsType} from '../RecommendsAction/actionFields';
import type {adHocFeeFieldType} from '../RecommendAdHocFee/adHocFeeFields';
import type {setUpProtectionFields} from '../RecommendsSetUpNew/protectionFields';
import type {setUpAnnuityFields} from '../RecommendsSetUpNew/annuityFields';

export type PlanActionFieldKeys =
  | 'setUpNew'
  | 'modify'
  | 'contribute'
  | 'transfer'
  | 'withdraw'
  | 'action'
  | 'adHocFee';

export type recommendFieldsType = {
  // ============================================
  // PLAN ACTION SPECIFIC FIELDS
  // ============================================

  setUpNew?: setUpNewFields;
  modify?: modifyFieldsType;
  contribute?: contributeFieldType;
  transfer?: transferFieldsType;
  withdraw?: withdrawalFieldsType;
  action?: actionFieldsType;
  adHocFee?: adHocFeeFieldType;
  protectionPlan?: setUpProtectionFields;
  annuity?: setUpAnnuityFields;

  // ============================================
  // SHARED HEADER FIELDS
  // Used by CreateRecommend header layer across all plan actions
  // ============================================

  isSppDa: boolean;

  // USED BY: Shared (CreateRecommendHeader, CreateRecommendSPPDAHeader, RecommendHowOptions, RecommendPlanOptions, CreateRecommendFooter, useSaveRecommendation)
  how: keyof typeof RECOMMENDATION_TYPES;

  // USED BY: Shared (CreateRecommendHeader, CreateRecommendSPPDAHeader, RecommendSPPOptions, RecommendWhatOptions, CreateRecommendFooter, useSaveRecommendation)
  sppType: keyof typeof SPP_RECOMMENDATION_TYPES | undefined;

  // USED BY: Shared (CreateRecommendHeader, CreateRecommendSPPDAHeader, RecommendWhoOptions, utils, CreateRecommendHelpers) + Transfer (TransferHelpers)
  contact: string | undefined;

  // USED BY: Shared (CreateRecommendHeader, CreateRecommendSPPDAHeader, RecommendWhatOptions, recommendValidation, CreateRecommendHelpers, PlanTypeContainer) + New Plan (NewPlanForm, NewSubPlanDetails)
  planAction: OVRecommendActionEnum | undefined;

  // USED BY: Shared (CreateRecommendHeader, CreateRecommendSPPDAHeader, RecommendPlanOptions, CreateRecommendHelpers, CreateRecommendFooter, naming, utils)
  //        + Contribute (ContributeSources, ContributeHelpers, contributeHooks, contributeValidators, getContributionVariables)
  //        + Transfer (Transfer, TransferReason, transferHooks, transferValidator, getTransferVariables)
  //        + Withdraw (Withdrawal, WithdrawalSources, withdrawalFormFields, WithdrawalValidators, getWithdrawalVariables)
  //        + New Plan (SetUpNew, NewPlanForm, NewSubPlanDetails, setUpNewHooks, setUpNewValidator, getNewVariables)
  //        + Ad Hoc Fee (RecommendAdHocFee)
  //        + Modify (getModifyVariables)
  mainPlanType: mainPlanTypeType;

  // ============================================
  // SHARED HEADER FIELDS (continued)
  // ============================================

  // USED BY: Shared (createRecommendHooks) + Save (useSaveRecommendation) + validators (contributeValidators, setUpNewValidator, transferValidator, WithdrawalValidators)
  goal: ListGoalBasic_goals | null | undefined;

  // USED BY: Shared (createRecommendHooks)
  urgency: string;

  // USED BY: Shared (createRecommendHooks)
  urgencyReason: string;

  // ============================================
  // METADATA FIELDS
  // ============================================

  // USED BY: Save (useSaveRecommendation) + PlanFees
  id?: string;

  // // USED BY: Save (useSaveRecommendation)
  // parentId?: string;

  // USED BY: Save (getRecommendationVariables values?.note) + CreateRecommend initial values
  note?: string;

  // USED BY: Contribute (Contribute) + Shared (CreateRecommendFooter, PlanTypeContainer)
  forcedMainPlanType: string | undefined | 'pension';

  // ============================================
  // DERIVED BOOLEAN FLAGS
  // ============================================

  // USED BY: Contribute (contributeHooks, contributeValidators) + Transfer (transferHooks, transferValidator) + New Plan (setUpNewHooks) + ShellPlan + naming
  // SET BY: contributeHooks, transferHooks, setUpNewHooks, ShellPlan
  isLisa?: boolean;

  isMinor?: boolean;

  // USED BY: New Plan (NewPlanForm via investorCategoryField AutoField) + CreateRecommend initial values
  investorCategory?: string;

  /**
   * Description
   * Remove sub plan selection when setting up a SIPP structured products
   * Why is this needed?
   * Structure of the Lowes SIPP with a Structured product slightly differs for structured products.
   */
  // USED BY: New Plan only (setUpNewHooks, NewPlanForm, SetUpNew) + validators
  //          (setUpNewValidator, transferValidator, contributeValidators)
  // SET BY: setUpNewHooks
  isLowesPension?: boolean;

  /**
   * Description
   * Remove sub plan selection when setting up a Transact onshore bond
   * Why is this needed?
   * Transact onshore bonds should be able to be submitted without selecting an underlying GIA
   */
  // USED BY: New Plan only (setUpNewHooks, NewPlanForm) + validators
  //          (setUpNewValidator, transferValidator, contributeValidators)
  // SET BY: setUpNewHooks
  isTransactBond?: boolean;
};

/**
 * same as BE's product_type_options
 */
export const productTypes = [
  'pension',
  'gia',
  'isa',
  'bond',
  'jisa',
  'sub',
  'lisa',
  'jsipp',
] as const;
export type productTypeOptions = typeof productTypes[number];

export type mainPlanTypeType =
  | 'pension'
  | 'gia'
  | 'isa'
  | 'bond'
  | 'jisa'
  | 'jsipp'
  | 'annuity'
  | 'protectionPlan';

// ============================================
// SHARED TYPES
// ============================================

export type MomentDate = Date | Moment | null | undefined; // done

export type FeeType = 'GBP' | '%';

export type DAFeeFieldType = {
  fee: string;
  feeType: FeeType | null;
  fixedFee?: string;
  percentageFee?: string;
};

export type initialFeesFieldType = string | undefined | null;

export type planAndSubplanAmounts = {
  planProvider?: string | null;
  singleClientAmount: moneyFieldValueType;
  recurringClientAmount?: moneyFieldValueType;
  singleEmployerAmount?: moneyFieldValueType;
  recurringEmployerAmount?: moneyFieldValueType;
};
